import { useCallback, useEffect, useState } from "react";
import { Loading } from "../Loading";
import {
  driftedPins,
  formatWhen,
  groupLabel,
  loadModels,
  readTally,
  totalReadings,
  valueLabel,
  type ModelsPayload,
  type SurfaceView,
} from "../../api/models";

/**
 * AI MODELS. The design behind this screen draws a live waveform, a latency
 * figure, a speech accuracy reading, a rubric weighting matrix and three
 * calibration sliders. Nothing streams, nothing is timed, nothing measures
 * accuracy and no code reads a rubric weight, so none of them is drawn here.
 *
 * What is drawn, per surface, is what is recorded: the model pin the provider
 * reported, the attempts behind it, and the prompt, built by the same function
 * the reader calls so it cannot go stale. The rest is listed at the foot of
 * the screen with its blocker, and again in `app/DEFERRED.md`.
 */

const PANEL = "bg-surface-container-lowest border border-outline-variant rounded-xl";
const LABEL = "font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant";
const CELL = "py-3 px-3 align-top font-body-sm text-body-sm";
const HEAD = "py-2.5 px-3 text-left font-label-sm text-label-sm uppercase tracking-wider";
const QUIET =
  "px-3.5 py-2 rounded-lg font-body-sm text-body-sm font-semibold bg-surface-container border border-outline-variant text-on-surface hover:bg-surface-container-high transition-colors disabled:opacity-50";

export function AiModelsScreen() {
  const [data, setData] = useState<ModelsPayload | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [kind, setKind] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const payload = await loadModels(kind);
      setData(payload);
      setFailure(null);
      setOpen((current) => current ?? payload.prompts[0]?.key ?? null);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : "the server did not answer");
    }
  }, [kind]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const pinsSeen =
    data === null
      ? 0
      : new Set(data.surfaces.flatMap((one) => one.pins.map((pin) => pin.modelPin))).size;
  const readings =
    data === null ? 0 : data.surfaces.reduce((sum, one) => sum + totalReadings(one), 0);
  const givenUp = data === null ? 0 : data.surfaces.reduce((sum, one) => sum + one.givenUp, 0);
  const attempts = data === null ? 0 : data.surfaces.reduce((sum, one) => sum + one.attempts, 0);

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-space-lg">
      <header className="flex flex-col xl:flex-row xl:items-end justify-between gap-space-md border-b border-outline-variant pb-space-md">
        <div className="space-y-1">
          <div className={LABEL}>Executive supervision, the model fleet as recorded</div>
          <h1 className="font-headline-lg text-headline-lg text-on-surface font-bold tracking-tight">
            Which model ran, and on what
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant max-w-3xl">
            Four surfaces call a model. Each one pins the model id the provider reported onto the
            row it produced, so every reading in the product can be traced to the exact model that
            made it. This screen reports those pins, the attempts behind them, and the prompts the
            model was given.
          </p>
        </div>
        {/* NOT BEFORE THE READ LANDS. Reloading a screen that is still on its
            first read does nothing a person can see. */}
        {data === null ? null : (
          <button type="button" className={QUIET} onClick={() => void reload()}>
            Reload
          </button>
        )}
      </header>

      {failure === null ? null : (
        <p
          role="status"
          aria-live="polite"
          className="rounded-lg border border-error/40 bg-error-container text-on-error-container px-space-md py-space-sm font-body-sm text-body-sm"
        >
          {failure}
        </p>
      )}

      {data === null ? (
        <Loading what="Reading the model pins." />
      ) : (
        <>
          <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-space-md">
            <Kpi label="Surfaces calling a model" value={String(data.surfaces.length)} note="Each pins the model that answered" />
            <Kpi label="Readings recorded" value={String(readings)} note="Rows carrying a model pin" />
            <Kpi
              label="Model calls attempted"
              value={String(attempts)}
              note="Counted failures, not successes"
            />
            <Kpi label="Given up on" value={String(givenUp)} note="Unread after the attempt cap" />
          </section>

          <section className={`${PANEL} p-space-lg space-y-space-md`}>
            <div>
              <h2 className="font-headline-sm text-headline-sm text-on-surface font-semibold">
                The fleet, per surface
              </h2>
              <p className="font-body-sm text-body-sm text-on-surface-variant mt-0.5">
                {pinsSeen === 0
                  ? "Nothing has been read yet, so no pin exists to report. The configured model is what the next call will use."
                  : `${pinsSeen} distinct model pin${pinsSeen === 1 ? "" : "s"} across every row in the database.`}
              </p>
            </div>
            <div className="space-y-space-md">
              {data.surfaces.map((surface) => (
                <Surface key={surface.key} surface={surface} />
              ))}
            </div>
          </section>

          <section className={`${PANEL} p-space-lg space-y-space-md`}>
            <div>
              <h2 className="font-headline-sm text-headline-sm text-on-surface font-semibold">
                The prompts, as the code builds them
              </h2>
              <p className="font-body-sm text-body-sm text-on-surface-variant mt-0.5">
                Built by the same functions the readers call, with the variable parts named in
                braces rather than filled in. The closed skill list carries{" "}
                {data.vocabulary} entries and is read from the database, not retyped here. These are
                read only: a prompt is changed in the domain layer, where it is under test.
              </p>
            </div>

            {data.vocabularyGroups.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-space-md">
                {data.vocabularyGroups.map((group) => (
                  <div
                    key={group.column}
                    className="border border-outline-variant rounded-lg bg-surface-container-low p-space-md space-y-space-xs"
                  >
                    <span className={LABEL}>{groupLabel(group.column)}</span>
                    <ul className="space-y-1">
                      {group.values.map((one) => (
                        <li
                          key={one.value}
                          className="flex justify-between gap-space-sm font-body-sm text-body-sm"
                        >
                          <span className="text-on-surface truncate">{valueLabel(one.value)}</span>
                          <span className="text-on-surface-variant tabular-nums">{one.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <p className="md:col-span-3 font-body-sm text-body-sm text-on-surface-variant">
                  Those columns are read off the skills table itself, not listed in this console. A
                  discipline or a domain added by a migration appears here without a line of the
                  admin console changing, and one removed stops appearing.
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-space-xs">
              {data.prompts.map((prompt) => (
                <button
                  key={prompt.key}
                  type="button"
                  onClick={() => setOpen(prompt.key)}
                  aria-pressed={open === prompt.key}
                  className={[
                    "px-3 py-1.5 rounded-lg font-body-sm text-body-sm transition-colors",
                    open === prompt.key
                      ? "bg-primary text-on-primary font-semibold"
                      : "bg-surface-container text-on-surface-variant hover:text-on-surface",
                  ].join(" ")}
                >
                  {prompt.label}
                </button>
              ))}
            </div>

            {data.prompts
              .filter((prompt) => prompt.key === open)
              .map((prompt) => (
                <div key={prompt.key} className="space-y-space-sm">
                  <dl className="flex flex-wrap gap-x-space-lg gap-y-1 font-body-sm text-body-sm">
                    <div className="flex gap-space-xs">
                      <dt className="text-on-surface-variant">Source</dt>
                      <dd className="font-label-md text-label-md text-on-surface break-all">
                        {prompt.source}
                      </dd>
                    </div>
                    <div className="flex gap-space-xs">
                      <dt className="text-on-surface-variant">Used by</dt>
                      <dd className="text-on-surface">{prompt.usedBy.join(", ")}</dd>
                    </div>
                  </dl>
                  {prompt.kind === null ? null : (
                    <label className="flex flex-wrap items-center gap-space-sm font-body-sm text-body-sm text-on-surface-variant">
                      <span>Round being rehearsed</span>
                      <select
                        value={data.kind}
                        onChange={(event) => setKind(event.target.value)}
                        className="bg-surface-container-low border border-outline-variant rounded-lg px-3 py-1.5 text-on-surface"
                      >
                        {data.kinds.map((one) => (
                          <option key={one.key} value={one.key}>
                            {one.label}
                          </option>
                        ))}
                      </select>
                      <span>
                        The list is the rehearsable kinds the domain declares, so a kind added there
                        appears here.
                      </span>
                    </label>
                  )}
                  <pre className="max-h-[28rem] overflow-auto bg-surface-container-low border border-outline-variant rounded-lg p-space-md font-label-md text-label-md text-on-surface whitespace-pre-wrap">
                    {prompt.text}
                  </pre>
                </div>
              ))}
          </section>

        </>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  note,
}: {
  readonly label: string;
  readonly value: string;
  readonly note: string;
}) {
  return (
    <div className={`${PANEL} p-space-md flex flex-col gap-space-xs`}>
      <span className={LABEL}>{label}</span>
      <div className="font-headline-md text-headline-md text-on-surface font-bold tracking-tight">
        {value}
      </div>
      <p className="font-body-sm text-body-sm text-on-surface-variant">{note}</p>
    </div>
  );
}

function Surface({ surface }: { readonly surface: SurfaceView }) {
  const drifted = driftedPins(surface);
  return (
    <article className="border border-outline-variant rounded-lg bg-surface-container-low p-space-md space-y-space-sm">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-space-sm">
        <div>
          <h3 className="font-headline-sm text-headline-sm text-on-surface font-semibold">
            {surface.label}
          </h3>
          <p className="font-body-sm text-body-sm text-on-surface-variant max-w-2xl">
            {surface.what}
          </p>
        </div>
        <dl className="shrink-0 font-body-sm text-body-sm space-y-0.5 lg:text-right">
          <Pair term="Configured" detail={surface.configuredModel} mono />
          <Pair
            term="Extraction version"
            detail={surface.configuredVersion ?? "not versioned"}
            mono
          />
          <Pair term="Table" detail={surface.table} mono />
        </dl>
      </div>

      <div className="flex flex-wrap items-center gap-x-space-lg gap-y-1 font-body-sm text-body-sm text-on-surface-variant">
        <span className="text-on-surface font-semibold">{readTally(surface)}</span>
        <span>{surface.pending} waiting</span>
        <span>
          {surface.givenUp} given up after {surface.maxAttempts} tries
        </span>
        <span>{surface.attempts} failed calls counted</span>
      </div>

      {surface.pins.length === 0 ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          No pin yet. Nothing on this surface has been read.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-container text-on-surface-variant">
              <tr>
                <th className={HEAD}>Model pin, as the provider reported it</th>
                <th className={HEAD}>Version</th>
                <th className={`${HEAD} text-right`}>Rows</th>
                <th className={`${HEAD} text-right`}>Last</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/60">
              {surface.pins.map((pin) => (
                <tr key={`${pin.modelPin}:${pin.version ?? ""}`}>
                  <td className={`${CELL} font-label-md text-label-md text-on-surface break-all`}>
                    {pin.modelPin}
                    {pin.modelPin === surface.configuredModel ? null : (
                      <span className="ml-2 px-1.5 py-0.5 rounded border border-outline-variant bg-surface-container-high font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">
                        not the current pin
                      </span>
                    )}
                  </td>
                  <td className={`${CELL} font-label-md text-label-md`}>{pin.version ?? "none"}</td>
                  <td className={`${CELL} text-right`}>{pin.readings}</td>
                  <td className={`${CELL} text-right`}>{formatWhen(pin.lastAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drifted.length === 0 ? null : (
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          {drifted.length} pin{drifted.length === 1 ? "" : "s"} here predate the constant the code
          now carries. That is expected, and it is the reason the pin is stored: a reading is
          answerable for the model that made it, not for the one configured today.
        </p>
      )}
    </article>
  );
}

function Pair({
  term,
  detail,
  mono,
}: {
  readonly term: string;
  readonly detail: string;
  readonly mono?: boolean;
}) {
  return (
    <div className="flex gap-space-xs lg:justify-end">
      <dt className="text-on-surface-variant">{term}</dt>
      <dd
        className={mono === true ? "font-label-md text-label-md text-on-surface" : "text-on-surface"}
      >
        {detail}
      </dd>
    </div>
  );
}

